import { useCallback } from 'react'

import { TasksValueObjectsTaskStatusEnum } from 'generated/resources/datatable.graphql'

import {
  TaskActionsHook,
  TaskCustomComponent,
  TaskDefinition,
} from 'modules/inbox_tasks/lib/abstraction'
import { useUpdateTaskStatuses } from 'modules/inbox_tasks/lib/updateTaskStatus/updateTaskStatus'

import BasicTaskContent from './BasicTask'
import { getBasicTaskMetadata } from './BasicTask/metadata'
import { useCompleteStepActionTask } from './completeTask/completeTask'
import { ActionType, StepActionContentComponent, parseConfigurationJson } from './helpers'
import TrainingContent from './Training'
import { getTrainingMetadata } from './Training/metadata'
import UploadDocumentContent from './UploadDocument'
import { getUploadDocumentMetadata } from './UploadDocument/metadata'

const ACTION_COMPONENTS: Record<ActionType, StepActionContentComponent> = {
  basic_task: BasicTaskContent,
  training: TrainingContent,
  upload_document: UploadDocumentContent,
}

const isActionType = (value: string): value is ActionType => value in ACTION_COMPONENTS

const EmployeeLifecycleStepActionContent: TaskCustomComponent<'EmployeeLifecycleStepAction'> = ({
  taskId,
  taskable,
}) => {
  const rawActionType = taskable?.actionType ?? 'basic_task'
  const Component = isActionType(rawActionType)
    ? ACTION_COMPONENTS[rawActionType]
    : ACTION_COMPONENTS.basic_task

  return (
    <Component
      taskId={taskId}
      stepActionId={taskable?.id}
      files={taskable?.uploadedDocuments ?? []}
    />
  )
}

const useEmployeeLifecycleStepActions: TaskActionsHook<'EmployeeLifecycleStepAction'> = ({
  taskId,
  status,
  taskable,
}) => {
  const updateTaskStatuses = useUpdateTaskStatuses()
  const completeStepActionTask = useCompleteStepActionTask()

  const handleDropdownAction = useCallback(
    async ({ actionId }: { actionId: string }) => {
      const statuses: Record<string, TasksValueObjectsTaskStatusEnum> = {
        'mark-as-done': 'done',
        'mark-as-todo': 'todo',
        'mark-as-in-progress': 'in_progress',
        'mark-as-discarded': 'discarded',
      }

      const newStatus = statuses[actionId]

      if (!newStatus) return

      if (newStatus === 'done' && taskable?.id) {
        await completeStepActionTask({
          stepActionId: taskable.id,
          taskId,
        })
        return true
      }

      await updateTaskStatuses({
        taskIds: [taskId],
        status: newStatus,
      })

      return true
    },
    [completeStepActionTask, taskId, taskable?.id, updateTaskStatuses]
  )

  return {
    onClickDropdownAction: handleDropdownAction,
  }
}

export const EmployeeLifecycleStepActionTaskDefinition: TaskDefinition<'EmployeeLifecycleStepAction'> =
  {
    taskableTypename: 'EmployeeLifecycleStepAction',
    initialState: {},
    component: EmployeeLifecycleStepActionContent,
    useActions: useEmployeeLifecycleStepActions,
    getUrl: ({ info, navigation }) => {
      if (info.actionType !== 'training') return null

      const config = parseConfigurationJson(info.configurationJson)
      const trainingId = config?.training_id

      if (!trainingId) return null

      return navigation.trainings.list.details_revamp.view.participants.fullPath({
        id: String(trainingId),
      })
    },
    getMetadata: ({ info }) => {
      switch (info.actionType) {
        case 'training':
          return getTrainingMetadata()
        case 'upload_document':
          return getUploadDocumentMetadata(info.configurationJson)
        case 'basic_task':
        default:
          return getBasicTaskMetadata(info.configurationJson)
      }
    },
  }
